/**
 * Monitoring API Response Mappers
 *
 * Notification config API uses lowercase keys (matches monitoring-svc backend)
 * See: monitoring-svc/context/config/NotificationConfig.go
 */
import { reverseMap, transformKeys } from '@/shared/mappers/base.mapper';

import type { MonitoringConfig, NotificationConfig } from '@/types';

// =============================================================================
// Notification Config - Backend uses lowercase keys
// =============================================================================

export interface NotificationConfigApi {
  id?: number;
  tenantid?: number;
  appid?: number;
  environmentid?: number;
  emailaddresses?: string;
  success?: boolean;
  failure?: boolean;
  partialSuccess?: boolean;
  eventtype?: number;
}

export interface SaveNotificationConfigPayload {
  id?: number;
  tenantid: number;
  appid: number;
  environmentid: number;
  emailaddresses: string;
  success: boolean;
  failure: boolean;
  partialSuccess: boolean;
  eventtype: number;
}

export const fromNotificationConfigResponse = (
  response: NotificationConfigApi | null
): NotificationConfig | null => {
  if (!response || !response.id) return null;
  return {
    id: response.id,
    tenantId: response.tenantid ?? 0,
    appId: response.appid ?? 0,
    environmentId: response.environmentid ?? 0,
    emailAddresses: response.emailaddresses ?? '',
    success: response.success ?? false,
    failure: response.failure ?? false,
    partialSuccess: response.partialSuccess ?? false,
    eventType: response.eventtype ?? 0,
  };
};

export const toSaveNotificationConfigPayload = (data: {
  tenantId: string;
  appId: string;
  environmentId: string;
  id?: number;
  emailAddresses: string;
  success: boolean;
  failure: boolean;
  partialSuccess: boolean;
}): SaveNotificationConfigPayload => {
  const eventtype = computeEventType(data.success, data.failure, data.partialSuccess);
  return {
    ...(data.id && { id: data.id }),
    tenantid: parseInt(data.tenantId, 10),
    appid: parseInt(data.appId, 10),
    environmentid: parseInt(data.environmentId, 10),
    emailaddresses: data.emailAddresses,
    success: data.success,
    failure: data.failure,
    partialSuccess: data.partialSuccess,
    eventtype,
  };
};

export const toDeleteNotificationConfigPayload = (data: {
  tenantId: string;
  appId: string;
  environmentId: string;
}) => ({
  tenantid: parseInt(data.tenantId, 10),
  appid: parseInt(data.appId, 10),
  environmentid: parseInt(data.environmentId, 10),
});

/** eventtype: 0=success, 1=failure, 2=both, 3=none */
function computeEventType(success: boolean, failure: boolean, partialSuccess: boolean): number {
  if (success && failure) return 2;
  if (success) return 0;
  if (failure || partialSuccess) return 1;
  return 3;
}

// =============================================================================
// Legacy Monitoring Config (for other endpoints)
// =============================================================================

const MONITORING_CONFIG_FIELDS = {
  Id: 'id',
  TenantId: 'tenantId',
  AppId: 'appId',
  EnvironmentId: 'environmentId',
  Enabled: 'enabled',
  Frequency: 'frequency',
  Recipients: 'recipients',
  CreatedAt: 'createdAt',
  UpdatedAt: 'updatedAt',
};

export const transformGetMonitoringConfigResponse = (response: unknown): MonitoringConfig => {
  return transformKeys<MonitoringConfig>(response, MONITORING_CONFIG_FIELDS, {
    parseJson: false,
    recursive: true,
  });
};

export const transformSaveMonitoringConfigRequest = (data: unknown): unknown => {
  const reverseFields = reverseMap(MONITORING_CONFIG_FIELDS);
  return transformKeys(data, reverseFields, {
    parseJson: false,
    recursive: true,
  });
};

export const transformDeleteMonitoringConfigRequest = (data: unknown): unknown => {
  const reverseFields = reverseMap(MONITORING_CONFIG_FIELDS);
  return transformKeys(data, reverseFields, {
    parseJson: false,
    recursive: true,
  });
};
